import { Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlotT } from '@/cms/useSlotT'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Button } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import CmsImage from '@/components/media/CmsImage'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import { uuidFromUploadPath } from '@/utils/mediaPath'
import { safeAssetPath, safeExternalUrl } from '@/utils/safeExternalUrl'
import type {
  ArticleLinkMagazineIssue,
  ExternalMagazineIssue,
  ImageMagazineIssue,
  MagazineIssue,
  PdfMagazineIssue,
} from '@/content/magazines'

/**
 * Lazy-load the PDF viewer + its `pdfjs-dist` dependency.
 *
 * Why: `pdfjs-dist@4` constructs a transport-layer worker during its
 * module evaluation. Pulling it into the main bundle via a static
 * import caused `/magazine/` (which renders this card on every archive
 * page visit) to abort React initialisation with "No
 * GlobalWorkerOptions.workerSrc specified" in prod — PDF cards are at
 * most 1 of N entries on the listing, and the listing still renders the
 * cover + meta for PDF rows before any viewer would mount. The lazy
 * import keeps pdfjs out of the initial chunk entirely on routes
 * where the user never opens an embedded reader.
 */
const PdfViewer = lazy(() => import('@/components/magazine/PdfViewer'))

/**
 * The `sizes` for a magazine cover.
 *
 * The cover frame is `md:w-96` — 384px — on desktop and the card's full width
 * below the md breakpoint, where '100vw' overstates it (the card sits inside
 * the 1200px container with its own padding). Overstating is safe; a `sizes`
 * that is too small would make the browser upscale.
 */
const COVER_SIZES = '(min-width: 768px) 384px, 100vw'

/**
 * Every card title is pinned to ONE colour (2026-11-04).
 *
 * Two of the four archive titles are links and two are plain text, and the
 * global `a { color: var(--color-primary) }` rule made the linked ones
 * green while the plain ones took the heading ink — the archive read as
 * two different kinds of row. The explicit colour on the H3 and on each
 * anchor flattens all four to the same plum.
 */
const TITLE_COLOR_CLASS = 'text-[#341A20]'

type AnyMagazine =
  | PdfMagazineIssue
  | ExternalMagazineIssue
  | ImageMagazineIssue
  | ArticleLinkMagazineIssue

/**
 * MagazineArticleCard — one entry in the press-coverage archive.
 *
 * Layout varies by article kind:
 *   - PDF      → embedded <PdfViewer> below the editorial block
 *   - image    → link to /magazine/:slug
 *   - external → opens publisher site in a new tab
 *
 * Phase 1 mobile overhaul (2026-10-13):
 *   - Lifted out of MagazinePage.tsx so it can be reused (homepage
 *     MagazineFeature.tsx may want it later).
 *   - Cover frame capped at `max-h-80` (320px) so a tall newspaper-page
 *     scan can't push the editorial block below the fold.
 *   - PDF viewer anchor uses the shared `--sticky-header-offset` token
 *     (mobile 5rem, desktop 128px) instead of the hard-coded 8rem that
 *     overshot on phones.
 */
export default function MagazineArticleCard({ mag }: { mag: AnyMagazine }) {
  const { t, i18n } = useTranslation()
  const slotT = useSlotT('magazine')
  const isPdf = mag.kind === 'pdf'
  const isImage = mag.kind === 'image'
  const publicationLabel = i18n.language.startsWith('zh') ? mag.publication : mag.publicationEn
  const issueLabel = i18n.language.startsWith('zh') ? mag.issue : mag.issueEn
  const coverAlt = t('magazine.coverAlt', { publication: publicationLabel, issue: issueLabel })
  const detailPath = `/magazine/${mag.slug}`

  const formattedDate = (() => {
    try {
      return new Date(mag.date).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return mag.date
    }
  })()

  /**
   * Cover frame: bucketed by image aspect ratio (so readability is
   * preserved without aggressive cropping) and capped at max-h-80
   * (320px) so a very tall scan can't dominate the list.
   *
   * Buckets:
   *   • very tall  (w/h <  0.55) → 3:4 frame (was 2:3 — the 3:4 + max-h
   *                                 cap keeps the cell ≤ 320px on mobile)
   *   • everything else         → 4:3 frame
   *
   * `object-contain` so no cover is cropped — only sized inside its frame.
   */
  const wDim = typeof mag.coverWidth === 'number' ? mag.coverWidth : 0
  const hDim = typeof mag.coverHeight === 'number' ? mag.coverHeight : 0
  const hasDims = wDim > 0 && hDim > 0
  const ratio = hasDims ? wDim / hDim : 0
  const isVeryTall = hasDims && ratio < 0.55
  const aspectClass = isVeryTall ? 'aspect-[3/4]' : 'aspect-[4/3]'
  const coverFrameClassName = `block w-full md:w-96 ${aspectClass} max-h-96 overflow-hidden mx-auto md:mx-0 rounded-yada-lg border border-border shadow-yada hover:shadow-yada-lg transition-shadow focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 flex-shrink-0 bg-surface`
  const coverImgClassName = 'block w-full h-full object-contain'

  // A CMS cover is a media-library upload, so the responsive ladder can
  // serve it: the frame is 384px wide on desktop (md:w-96), which the
  // 1024px file it used to load never needed. A STATIC cover (/media/…) has
  // no media row behind it and keeps its plain <img>.
  const coverMediaUuid =
    mag.coverMediaUuid ?? (mag.coverImage ? uuidFromUploadPath(mag.coverImage) : null)
  const hasResponsiveCover = coverMediaUuid
    ? Boolean(cmsMediaLargestWebpUrl(coverMediaUuid))
    : false

  const coverImg = mag.coverImage ? (
    hasResponsiveCover ? (
      <CmsImage
        uuid={coverMediaUuid}
        alt={coverAlt}
        sizes={COVER_SIZES}
        className={coverImgClassName}
      />
    ) : (
      <img
        src={mag.coverImage}
        alt={coverAlt}
        loading="lazy"
        decoding="async"
        width={mag.coverWidth}
        height={mag.coverHeight}
        className={coverImgClassName}
      />
    )
  ) : null

  /* ============================================================
     The CMS-controlled URLs, validated before they become an href.

     `externalHref` is the publisher's article — a link that LEAVES this
     site — and `pdfSrc` is the clipping this site serves. Both come from
     the CMS, so both are checked: a `javascript:` value would otherwise
     run script in every visitor's browser when the link is clicked. A
     value that fails the check degrades to "no link" / "no file" rather
     than to an executable one.
     ============================================================ */
  const externalHref = safeExternalUrl(mag.externalUrl)
  const pdfSrc = mag.kind === 'pdf' ? safeAssetPath(mag.pdfPath) : undefined

  /**
   * The cover poster. It is a link when there is somewhere safe to go —
   * the entry's own page (image), the embedded viewer (pdf), or the
   * publisher's site (external / article-link). When the CMS value fails
   * the check the cover is rendered as a plain frame: no href at all.
   */
  const coverFrame = coverImg ? (
    isImage ? (
      <Link to={detailPath} aria-label={coverAlt} className={coverFrameClassName}>
        {coverImg}
      </Link>
    ) : isPdf ? (
      pdfSrc ? (
        <a
          href={`#${mag.slug}-viewer`}
          aria-label={coverAlt}
          className={coverFrameClassName}
        >
          {coverImg}
        </a>
      ) : (
        <div className={coverFrameClassName}>{coverImg}</div>
      )
    ) : externalHref ? (
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={coverAlt}
        className={coverFrameClassName}
      >
        {coverImg}
      </a>
    ) : (
      <div className={coverFrameClassName}>{coverImg}</div>
    )
  ) : null

  return (
    <article
      id={mag.slug}
      className="bg-surface-2 rounded-yada-xl border border-border shadow-yada"
      aria-labelledby={`${mag.slug}-title`}
    >
      {/* Phase 1 mobile: the article container now uses the new Card
          `density="regular"`-equivalent padding via p-5 md:p-6 — the
          mobile-condensed 20px is the right touch target on phones,
          desktop still gets 24px. */}
      <div className="p-5 md:p-6">
        <div className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-7 items-start">
          {/* Cover poster */}
          {coverFrame}

          {/* Editorial block */}
          <div className="min-w-0">
            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1 rounded-yada-full text-xs font-semibold">
                <Icon icon="lucide:book-open" className="text-sm" />
                {publicationLabel}
              </span>
              {issueLabel && (
                <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                  <Icon icon="lucide:hash" className="text-sm" />
                  {issueLabel}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                <Icon icon="lucide:calendar" className="text-sm" />
                <time dateTime={mag.date}>{formattedDate}</time>
              </span>
              {externalHref && (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                >
                  <Icon icon="lucide:external-link" className="text-sm" />
                  {t('magazine.card.readOn', { publication: publicationLabel })}
                </a>
              )}
            </div>

            {/* Title */}
            <H3
              id={`${mag.slug}-title`}
              className={`text-xl md:text-2xl leading-tight mb-2 ${TITLE_COLOR_CLASS}`}
            >
              {isImage ? (
                <Link
                  to={detailPath}
                  className={`${TITLE_COLOR_CLASS} hover:text-primary transition-colors`}
                >
                  {mag.title}
                </Link>
              ) : mag.kind === 'article-link' && externalHref ? (
                <a
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${mag.title}（外部連結）`}
                  className={`${TITLE_COLOR_CLASS} hover:text-primary transition-colors`}
                >
                  {mag.title}
                </a>
              ) : (
                mag.title
              )}
            </H3>

            {/* Interviewee */}
            <p className="text-sm text-primary font-semibold mb-3">
              {slotT('magazine.intervieweeLabel')}
              {mag.interviewee}
            </p>

            {/* Summary */}
            <p className="text-base text-ink-soft leading-relaxed mb-5">
              {mag.summary}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              {isPdf ? (
                <>
                  {pdfSrc && (
                    <Button
                      as="a"
                      href={`#${mag.slug}-viewer`}
                      variant="primary"
                      size="sm"
                      iconLeft={<Icon icon="lucide:book-open-text" />}
                      iconRight={<Icon icon="lucide:arrow-down" />}
                    >
                      {slotT('magazine.viewer.readFull')}
                    </Button>
                  )}
                  {pdfSrc && (
                    <Button
                      as="a"
                      href={pdfSrc}
                      download
                      variant="outline"
                      size="sm"
                      iconLeft={<Icon icon="lucide:download" />}
                    >
                      {slotT('magazine.viewer.download')}
                    </Button>
                  )}
                  {externalHref && (
                    <a
                      href={externalHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                    >
                      <Icon icon="lucide:external-link" />
                      {slotT('magazine.viewer.openExternal')}
                    </a>
                  )}
                </>
              ) : isImage ? (
                <>
                  <Link
                    to={detailPath}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-5 py-2.5 rounded-yada-full text-sm font-semibold hover:bg-primary-dark transition-all duration-200 hover:-translate-y-0.5 hover:shadow-yada-md focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                  >
                    <Icon icon="lucide:book-open-text" />
                    {slotT('magazine.viewer.readFull')}
                    <Icon icon="lucide:arrow-right" />
                  </Link>
                  {externalHref && (
                    <a
                      href={externalHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                    >
                      <Icon icon="lucide:external-link" />
                      {slotT('magazine.viewer.openExternal')}
                    </a>
                  )}
                </>
              ) : externalHref ? (
                <Button
                  as="a"
                  href={externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  size="sm"
                  iconLeft={<Icon icon="lucide:external-link" />}
                  iconRight={<Icon icon="lucide:arrow-right" />}
                >
                  {slotT('magazine.viewer.readFull')}
                </Button>
              ) : (
                // The publisher's URL failed the check, so the CTA falls
                // back to the entry's own page on this site rather than
                // becoming an executable link.
                <Button
                  as="a"
                  href={detailPath}
                  variant="primary"
                  size="sm"
                  iconLeft={<Icon icon="lucide:book-open-text" />}
                  iconRight={<Icon icon="lucide:arrow-right" />}
                >
                  {slotT('magazine.viewer.readFull')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Embedded PDF viewer — only for PDF entries. Anchor uses the
          shared sticky-header-offset token so the page scrolls to a
          position that doesn't sit under the navbar on either viewport.
          The viewer is lazy-loaded so its `pdfjs-dist` dependency does
          not enter the listing-page chunk graph; `Suspense` shows a
          tiny spinner while the chunk arrives. */}
      {isPdf && pdfSrc && (
        <div
          id={`${mag.slug}-viewer`}
          className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border scroll-mt-[var(--sticky-header-offset)]"
        >
          <Suspense
            fallback={
              <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
                <Icon icon="lucide:loader-2" className="animate-spin text-lg" />
                <span className="text-sm">正在載入 PDF 檢視器…</span>
              </div>
            }
          >
            <PdfViewer src={pdfSrc} title={mag.title} />
          </Suspense>
        </div>
      )}
    </article>
  )
}

/* Re-export the union type so consumers can type their props without
   reaching into the magazines content module. */
export type { AnyMagazine as MagazineIssueBase }
export type { MagazineIssue }
